<template>
  <div v-loading="loading" :class="ns.b()">
    <z-steps :active="step" finish-status="success" :class="ns.e('steps')">
      <z-step
        :title="t('z.import.stepSelectFile')"
        :description="t('z.import.stepSelectFileDesc')"
      />
      <z-step
        :title="t('z.import.stepValidate')"
        :description="stepValidateDescription"
      />
      <z-step
        :title="t('z.import.stepResult')"
        :description="t('z.import.stepResultDesc')"
      />
    </z-steps>

    <div :class="ns.e('body')">
      <div v-show="step === 0" :class="ns.e('step')">
        <div :class="ns.e('template-bar')">
          <div :class="ns.e('template-text')">
            <p :class="ns.e('template-desc')">
              {{ t('z.import.templateDescPrefix') }}
              <span :class="ns.e('required-mark')">*</span>
              {{ t('z.import.templateDescSuffix') }}
            </p>
          </div>
          <z-button type="primary" plain @click="handleDownloadTemplate">
            {{ t('z.import.downloadTemplate') }}
          </z-button>
        </div>

        <z-upload
          drag
          :auto-upload="false"
          accept=".xlsx,.xls"
          :limit="1"
          :file-list="fileList"
          @change="handleFileChange"
          @remove="handleFileRemove"
        >
          <div :class="ns.e('upload-tip')">
            <p>{{ t('z.import.uploadDrag') }}</p>
            <p :class="ns.e('upload-hint')">{{ t('z.import.uploadHint') }}</p>
          </div>
        </z-upload>
        <p v-if="selectedFile" :class="ns.e('file-name')">
          {{ t('z.import.selectedFile') }}：{{ selectedFile.name }}
        </p>
      </div>

      <div v-show="step === 1" :class="ns.e('step')">
        <div :class="ns.e('summary')">
          <span>{{
            t('z.import.totalRows', { count: parsedRows.length })
          }}</span>
          <span
            v-if="clientValidate || validationErrors.length > 0"
            :class="{ 'is-error': validationErrors.length > 0 }"
          >
            {{ t('z.import.errorRows', { count: validationErrors.length }) }}
          </span>
          <span v-else :class="ns.e('server-hint')">
            {{ t('z.import.serverValidateHint') }}
          </span>
          <span :class="ns.e('mode-hint')">{{ modeHint }}</span>
        </div>

        <z-empty
          v-if="!parsedRows.length"
          :description="t('z.import.noData')"
        />

        <import-univer-preview
          v-if="parsedRows.length"
          ref="univerPreviewRef"
          :columns="previewColumns"
          :rows="parsedRows"
          :max-rows="maxRows"
          :validation-errors="validationErrors"
        />

        <div v-if="validationErrors.length" :class="ns.e('error-list')">
          <div :class="ns.e('error-list-title')">
            {{ t('z.import.errorListTitle') }}
          </div>
          <ul :class="ns.e('error-list-body')">
            <li
              v-for="(error, index) in validationErrors"
              :key="`${error.rowIndex}-${error.field}-${index}`"
              :class="ns.e('error-list-item')"
            >
              <span :class="ns.e('error-list-row')">
                {{ t('z.import.rowNo') }} {{ error.rowIndex }}
              </span>
              <span
                v-if="error.title || error.field"
                :class="ns.e('error-list-field')"
              >
                {{ error.title || error.field }}
              </span>
              <span :class="ns.e('error-list-message')">{{
                error.message
              }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div v-show="step === 2" :class="ns.e('step')">
        <slot name="result" :rows="importedRows" :meta="importResultMeta">
          <z-result
            icon="success"
            :title="
              t('z.import.importSuccess', { count: importedRows.length })
            "
            :sub-title="importResultSubTitle"
          />
        </slot>
      </div>
    </div>

    <div :class="ns.e('footer')">
      <slot name="footer-prefix" />
      <template v-if="!$slots.footer">
        <z-button v-if="step < 2" @click="handleCancel">
          {{ t('z.import.impCancel') }}
        </z-button>
        <z-button v-if="step > 0 && step < 2" @click="step -= 1">
          {{ t('z.import.prevStep') }}
        </z-button>
        <z-button
          v-if="step === 0"
          type="primary"
          :disabled="!selectedFile"
          :loading="parsing"
          @click="handleParse"
        >
          {{ t('z.import.nextStep') }}
        </z-button>
        <z-button
          v-if="step === 1"
          type="primary"
          :disabled="
            !parsedRows.length ||
            (clientValidate && validationErrors.length > 0)
          "
          :loading="confirming"
          @click="handleConfirm"
        >
          {{ t('z.import.impConfirm') }}
        </z-button>
        <z-button v-if="step === 2" type="primary" @click="handleFinish">
          {{ t('z.import.finish') }}
        </z-button>
      </template>
      <slot v-else name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue'
import ZButton from '@zoryn/components/button'
import ZEmpty from '@zoryn/components/empty'
import ZResult from '@zoryn/components/result'
import ZSteps, { ZStep } from '@zoryn/components/steps'
import ZUpload from '@zoryn/components/upload'
import { ZMessage } from '@zoryn/components/message'
import { useLocale, useNamespace } from '@zoryn/hooks'
import type { UploadFile, UploadFiles } from '@zoryn/components/upload'
import { gridImportProps } from './grid-import'
import {
  getImportColumns,
  isExcelFile,
  parseExcelFile,
  validateImportRows,
} from './import'
import type { GridImportColumn, ImportRowError } from './types'

defineOptions({ name: 'ZGridImport' })

const ImportUniverPreview = defineAsyncComponent(
  () => import('./spreadsheet/import-univer-preview.vue')
)

const props = defineProps(gridImportProps)

const emit = defineEmits<{
  'download-template': []
  success: [rows: Record<string, unknown>[]]
  done: [rows: Record<string, unknown>[]]
  cancel: []
}>()

const { t } = useLocale()
const ns = useNamespace('grid-import')

const step = ref(0)
const parsing = ref(false)
const confirming = ref(false)
const selectedFile = ref<File | null>(null)
const fileList = ref<UploadFiles>([])
const parsedRows = ref<Record<string, unknown>[]>([])
const fieldsInFile = ref<string[]>([])
const fileColumns = ref<GridImportColumn[]>([])
const validationErrors = ref<ImportRowError[]>([])
const importedRows = ref<Record<string, unknown>[]>([])
const univerPreviewRef = ref<{
  readRows: () => Record<string, unknown>[]
} | null>(null)

const resolvedTemplateTitle = computed(
  () => props.templateTitle || t('z.import.templateTitle')
)
const previewColumns = computed(() => {
  if (step.value >= 1 && fileColumns.value.length) {
    return fileColumns.value
  }
  return getImportColumns(props.columns)
})

const activeValidateColumns = computed(() =>
  fileColumns.value.length ? fileColumns.value : props.columns
)

const effectiveImportMode = computed(() => props.importMode ?? 'insertTop')
const pageTitle = computed(() => `导入 - ${resolvedTemplateTitle.value}`)

const modeLabel = computed(() => {
  if (effectiveImportMode.value === 'insertTop') {
    return t('z.import.modes.insertTop')
  }
  if (effectiveImportMode.value === 'upsert') {
    return '更新插入'
  }
  return t('z.import.modes.covering')
})

const modeHint = computed(() =>
  t('z.import.modeHint', { mode: modeLabel.value })
)

const stepValidateDescription = computed(() =>
  props.clientValidate
    ? t('z.import.stepValidateDesc')
    : t('z.import.stepValidateDescServer')
)

const confirmMeta = computed(() => ({
  sourceFileName: selectedFile.value?.name,
}))

const importResultMeta = computed(() => ({
  ...confirmMeta.value,
  importMode: effectiveImportMode.value,
  importModeLabel: modeLabel.value,
}))

const importResultSubTitle = computed(() =>
  t('z.import.importResultDesc', {
    mode: modeLabel.value,
    file: selectedFile.value?.name ?? '—',
  })
)

function revalidate() {
  if (!props.clientValidate) return
  validationErrors.value = validateImportRows(
    parsedRows.value,
    activeValidateColumns.value,
    (title) => t('z.import.fieldRequired', { title }),
    { fieldsInFile: fieldsInFile.value }
  )
}

function syncRowsFromPreview() {
  const rows = univerPreviewRef.value?.readRows() ?? []
  parsedRows.value = rows
}

function handleDownloadTemplate() {
  emit('download-template')
}

function handleFileChange(_uploadFile: UploadFile, files: UploadFiles) {
  fileList.value = files.slice(-1)
  const raw = fileList.value[0]?.raw
  selectedFile.value = raw instanceof File ? raw : null
}

function handleFileRemove() {
  selectedFile.value = null
  fileList.value = []
}

async function handleParse() {
  if (!selectedFile.value) return
  if (!isExcelFile(selectedFile.value)) {
    ZMessage.warning(t('z.import.invalidExcel'))
    return
  }

  parsing.value = true
  try {
    const {
      rows,
      fieldsInFile: matchedFields,
      fileColumns: parsedColumns,
    } = await parseExcelFile(
      selectedFile.value,
      props.columns,
      props.sheetName || undefined
    )
    parsedRows.value = rows
    fieldsInFile.value = matchedFields
    fileColumns.value = parsedColumns
    if (!parsedColumns.length) {
      ZMessage.warning(t('z.import.noImportColumns'))
      return
    }
    if (parsedRows.value.length > props.maxRows) {
      ZMessage.warning(t('z.import.maxRowsReached', { count: props.maxRows }))
      parsedRows.value = parsedRows.value.slice(0, props.maxRows)
    }
    validationErrors.value = []
    if (props.clientValidate) {
      revalidate()
    }
    if (!parsedRows.value.length) {
      ZMessage.warning(t('z.import.noExcelData'))
      return
    }
    step.value = 1
  } catch {
    ZMessage.error(t('z.import.parseFailed'))
  } finally {
    parsing.value = false
  }
}

async function handleConfirm() {
  syncRowsFromPreview()
  if (!parsedRows.value.length) {
    ZMessage.warning(t('z.import.noData'))
    return
  }
  if (props.clientValidate && validationErrors.value.length > 0) return

  confirming.value = true
  try {
    if (props.clientValidate) {
      revalidate()
      if (validationErrors.value.length > 0) return
    }

    if (props.serverValidate) {
      const errors = await props.serverValidate(
        parsedRows.value,
        confirmMeta.value
      )
      if (errors?.length) {
        validationErrors.value = errors
        ZMessage.warning(t('z.import.serverValidateFailed'))
        return
      }
    }

    if (props.beforeConfirm) {
      await props.beforeConfirm(parsedRows.value, confirmMeta.value)
    }
    importedRows.value = parsedRows.value
    step.value = 2
    emit('success', importedRows.value)
  } catch (error) {
    ZMessage.error(
      (error as Error)?.message ?? t('z.import.serverValidateFailed')
    )
  } finally {
    confirming.value = false
  }
}

function handleFinish() {
  emit('done', importedRows.value)
}

function handleCancel() {
  resetState()
  emit('cancel')
}

function resetState() {
  step.value = 0
  parsing.value = false
  confirming.value = false
  selectedFile.value = null
  fileList.value = []
  parsedRows.value = []
  fieldsInFile.value = []
  fileColumns.value = []
  validationErrors.value = []
  importedRows.value = []
}

defineExpose({
  resetState,
  pageTitle,
  effectiveImportMode,
  getParsedRows: () => parsedRows.value,
})
</script>
